import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Function to print the summary table as PDF
export const printSummaryTableAsPDF = async (tableElement, fileName = 'खर्च_सारांश_तालिका') => {
  try {
    if (!tableElement) {
      throw new Error('Table element not found');
    }

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Unable to open print window');
    }

    // Get the table HTML
    const tableHTML = tableElement.outerHTML;

    // Create the print document
    const printDocument = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>खर्च सारांश तालिका</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              font-size: 12px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              font-weight: bold;
            }
            /* Header colors based on image */
            th.budget-header {
              background-color: #8B0000;
              color: white;
            }
            th.institution-header {
              background-color: #2C3E50;
              color: white;
            }
            th.total-header {
              background-color: #28A745;
              color: white;
            }
            /* Row colors based on image */
            tr.capital-total {
              background-color: #FFFACD;
            }
            tr.operational-total {
              background-color: #17A2B8;
              color: white;
            }
            tr.grand-total {
              font-weight: bold;
            }
            /* Total column styling */
            td.total-column {
              background-color: #D4EDDA;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .header h1 {
              margin: 0;
              color: #333;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .section-header {
              background-color: #e6e6e6;
              font-weight: bold;
              text-align: center;
            }
            .subtotal-row {
              background-color: #f9f9f9;
              font-weight: bold;
            }
            .grand-total-row {
              background-color: #e6f3ff;
              font-weight: bold;
            }
            .budget-code {
              font-weight: bold;
              width: 15%;
            }
            .budget-title {
              width: 40%;
            }
            .institution-value {
              text-align: right;
              width: 15%;
            }
            .total-value {
              text-align: right;
              font-weight: bold;
              width: 15%;
            }
            @media print {
              body {
                margin: 0;
                font-size: 10px;
              }
              table {
                page-break-inside: auto;
              }
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>खर्च सारांश तालिका</h1>
            <p>नयाँ डाटा थप्नुहोस् मोडेलबाट</p>
            <p>मिति: ${new Date().toLocaleDateString('ne-NP')}</p>
          </div>
          ${tableHTML}
        </body>
      </html>
    `;

    // Write the document to the new window
    printWindow.document.write(printDocument);
    printWindow.document.close();

    // Wait for the content to load
    printWindow.onload = () => {
      // Print the window
      printWindow.print();
      
      // Close the window after printing
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    };

    return { success: true };
  } catch (error) {
    console.error('Error printing table:', error);
    return { success: false, error: error.message };
  }
};

// Function to generate PDF from table data
export const generateSummaryTablePDF = async (allExpenditureItems, institutions, fileName = 'खर्च_सारांश_तालिका') => {
  try {
    const pdf = new jsPDF('landscape', 'mm', 'a4');
    
    // Set font for Nepali text
    pdf.setFont('helvetica');
    pdf.setFontSize(12);
    
    // Add title
    pdf.setFontSize(16);
    pdf.text('खर्च सारांश तालिका', 140, 20, { align: 'center' });
    pdf.setFontSize(10);
    pdf.text('नयाँ डाटा थप्नुहोस् मोडेलबाट', 140, 30, { align: 'center' });
    pdf.text(`मिति: ${new Date().toLocaleDateString('ne-NP')}`, 140, 40, { align: 'center' });
    
    // Calculate column widths
    const pageWidth = 297; // A4 landscape width in mm
    const margin = 10;
    const availableWidth = pageWidth - (2 * margin);
    const codeWidth = 25;
    const titleWidth = 60;
    const institutionWidth = (availableWidth - codeWidth - titleWidth - 25) / institutions.length;
    const totalWidth = 25;
    
    let yPosition = 60;
    const rowHeight = 8;
    
    // Add headers with color formatting from image
    pdf.setFontSize(10);
    
    // Budget code and title headers - dark reddish-brown (#8B0000)
    pdf.setFillColor(139, 0, 0);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    
    let xPosition = margin;
    pdf.rect(xPosition, yPosition, codeWidth, rowHeight, 'F');
    pdf.text('बजेट कोड', xPosition + 2, yPosition + 5);
    
    xPosition += codeWidth;
    pdf.rect(xPosition, yPosition, titleWidth, rowHeight, 'F');
    pdf.text('बजेट शिर्षक', xPosition + 2, yPosition + 5);
    
    // Institution headers - dark blue/teal (#2C3E50)
    pdf.setFillColor(44, 62, 80);
    institutions.forEach(institution => {
      xPosition += institutionWidth;
      pdf.rect(xPosition, yPosition, institutionWidth, rowHeight, 'F');
      pdf.text(institution.substring(0, 15), xPosition + 2, yPosition + 5);
    });
    
    // Total column header - bright green (#28A745)
    pdf.setFillColor(40, 167, 69);
    xPosition += institutionWidth;
    pdf.rect(xPosition, yPosition, totalWidth, rowHeight, 'F');
    pdf.text('जम्मा', xPosition + 2, yPosition + 5);
    
    yPosition += rowHeight;
    
    // Add Capital Expenditure Section
    pdf.setFillColor(230, 230, 230);
    pdf.rect(margin, yPosition, availableWidth, rowHeight, 'F');
    pdf.setFontSize(12);
    pdf.text('पुँजीगत खर्च जम्मा', margin + 2, yPosition + 5);
    yPosition += rowHeight;
    
    // Calculate capital totals
    const capitalTotals = institutions.map(institution => {
      return allExpenditureItems
        .filter(item => (item.item_type === 'capital' || 
                        (item.code_number && item.code_number.startsWith('01.'))) &&
                        item.institution_name === institution)
        .reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
    });
    
    const capitalGrandTotal = capitalTotals.reduce((sum, total) => sum + total, 0);
    
    // Add capital totals row - light yellow background (#FFFACD)
    pdf.setFillColor(255, 250, 205);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    xPosition = margin;
    pdf.rect(xPosition, yPosition, codeWidth, rowHeight, 'F');
    pdf.text('', xPosition + 2, yPosition + 5);
    
    xPosition += codeWidth;
    pdf.rect(xPosition, yPosition, titleWidth, rowHeight, 'F');
    pdf.text('पुँजीगत खर्च जम्मा', xPosition + 2, yPosition + 5);
    
    capitalTotals.forEach(total => {
      xPosition += institutionWidth;
      pdf.rect(xPosition, yPosition, institutionWidth, rowHeight, 'F');
      pdf.text(total.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
    });
    
    xPosition += institutionWidth;
    pdf.rect(xPosition, yPosition, totalWidth, rowHeight, 'F');
    pdf.text(capitalGrandTotal.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
    
    yPosition += rowHeight;
    
    // Add individual capital items - white background
    pdf.setFillColor(255, 255, 255);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    
    const capitalItems = allExpenditureItems.filter(item => 
      item.item_type === 'capital' || 
      (item.code_number && item.code_number.startsWith('01.'))
    );
    
    const uniqueCapitalCodes = [...new Set(capitalItems.map(item => item.code_number))];
    
    uniqueCapitalCodes.forEach(code => {
      if (yPosition > 180) {
        pdf.addPage();
        yPosition = 20;
      }
      
      const itemsWithCode = capitalItems.filter(item => item.code_number === code);
      const title = itemsWithCode[0]?.expense_title || itemsWithCode[0]?.title || '';
      
      xPosition = margin;
      pdf.rect(xPosition, yPosition, codeWidth, rowHeight, 'F');
      pdf.text(code, xPosition + 2, yPosition + 5);
      
      xPosition += codeWidth;
      pdf.rect(xPosition, yPosition, titleWidth, rowHeight, 'F');
      pdf.text(title.substring(0, 30), xPosition + 2, yPosition + 5);
      
      institutions.forEach(institution => {
        const institutionItems = itemsWithCode.filter(item => item.institution_name === institution);
        const total = institutionItems.reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
        
        xPosition += institutionWidth;
        pdf.rect(xPosition, yPosition, institutionWidth, rowHeight, 'F');
        pdf.text(total.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
      });
      
      const total = itemsWithCode.reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
      xPosition += institutionWidth;
      // Total column - light green background (#D4EDDA)
      pdf.setFillColor(212, 237, 218);
      pdf.rect(xPosition, yPosition, totalWidth, rowHeight, 'F');
      pdf.text(total.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
      
      yPosition += rowHeight;
    });
    
    // Add Operational Expenditure Section
    if (yPosition > 180) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.setFillColor(230, 230, 230);
    pdf.rect(margin, yPosition, availableWidth, rowHeight, 'F');
    pdf.setFontSize(12);
    pdf.text('संचालन खर्च जम्मा', margin + 2, yPosition + 5);
    yPosition += rowHeight;
    
    // Calculate operational totals
    const operationalTotals = institutions.map(institution => {
      return allExpenditureItems
        .filter(item => (item.item_type === 'operational' || 
                        (item.code_number && item.code_number.startsWith('02.'))) &&
                        item.institution_name === institution)
        .reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
    });
    
    const operationalGrandTotal = operationalTotals.reduce((sum, total) => sum + total, 0);
    
    // Add operational totals row - blue background (#17A2B8) with white text
    pdf.setFillColor(23, 162, 184);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    xPosition = margin;
    pdf.rect(xPosition, yPosition, codeWidth, rowHeight, 'F');
    pdf.text('', xPosition + 2, yPosition + 5);
    
    xPosition += codeWidth;
    pdf.rect(xPosition, yPosition, titleWidth, rowHeight, 'F');
    pdf.text('संचालन खर्च जम्मा', xPosition + 2, yPosition + 5);
    
    operationalTotals.forEach(total => {
      xPosition += institutionWidth;
      pdf.rect(xPosition, yPosition, institutionWidth, rowHeight, 'F');
      pdf.text(total.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
    });
    
    xPosition += institutionWidth;
    pdf.rect(xPosition, yPosition, totalWidth, rowHeight, 'F');
    pdf.text(operationalGrandTotal.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
    
    yPosition += rowHeight;
    
    // Add individual operational items - white background
    pdf.setFillColor(255, 255, 255);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    
    const operationalItems = allExpenditureItems.filter(item => 
      item.item_type === 'operational' || 
      (item.code_number && item.code_number.startsWith('02.'))
    );
    
    const uniqueOperationalCodes = [...new Set(operationalItems.map(item => item.code_number))];
    
    uniqueOperationalCodes.forEach(code => {
      if (yPosition > 180) {
        pdf.addPage();
        yPosition = 20;
      }
      
      const itemsWithCode = operationalItems.filter(item => item.code_number === code);
      const title = itemsWithCode[0]?.expense_title || itemsWithCode[0]?.title || '';
      
      xPosition = margin;
      pdf.rect(xPosition, yPosition, codeWidth, rowHeight, 'F');
      pdf.text(code, xPosition + 2, yPosition + 5);
      
      xPosition += codeWidth;
      pdf.rect(xPosition, yPosition, titleWidth, rowHeight, 'F');
      pdf.text(title.substring(0, 30), xPosition + 2, yPosition + 5);
      
      institutions.forEach(institution => {
        const institutionItems = itemsWithCode.filter(item => item.institution_name === institution);
        const total = institutionItems.reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
        
        xPosition += institutionWidth;
        pdf.rect(xPosition, yPosition, institutionWidth, rowHeight, 'F');
        pdf.text(total.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
      });
      
      const total = itemsWithCode.reduce((sum, item) => sum + parseFloat(item.q1_budget_demand || 0), 0);
      xPosition += institutionWidth;
      // Total column - light green background (#D4EDDA)
      pdf.setFillColor(212, 237, 218);
      pdf.rect(xPosition, yPosition, totalWidth, rowHeight, 'F');
      pdf.text(total.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
      
      yPosition += rowHeight;
    });
    
    // Add Grand Total - white background with bold text
    if (yPosition > 180) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.setFillColor(255, 255, 255);
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    
    xPosition = margin;
    pdf.rect(xPosition, yPosition, codeWidth, rowHeight, 'F');
    pdf.text('', xPosition + 2, yPosition + 5);
    
    xPosition += codeWidth;
    pdf.rect(xPosition, yPosition, titleWidth, rowHeight, 'F');
    pdf.text('जम्मा खर्च', xPosition + 2, yPosition + 5);
    
    institutions.forEach((_, index) => {
      const total = capitalTotals[index] + operationalTotals[index];
      xPosition += institutionWidth;
      pdf.rect(xPosition, yPosition, institutionWidth, rowHeight, 'F');
      pdf.text(total.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
    });
    
    const grandTotal = capitalGrandTotal + operationalGrandTotal;
    xPosition += institutionWidth;
    // Total column - light green background (#D4EDDA)
    pdf.setFillColor(212, 237, 218);
    pdf.rect(xPosition, yPosition, totalWidth, rowHeight, 'F');
    pdf.text(grandTotal.toLocaleString('en-IN'), xPosition + 2, yPosition + 5);
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const fullFileName = `${fileName}_${timestamp}.pdf`;
    
    // Save the PDF
    pdf.save(fullFileName);
    
    return { success: true, fileName: fullFileName };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error.message };
  }
};
